import React, { useState, useEffect } from "react";
import Dialog from "../../CoreComponent/Dialog";
import httpService from "../../common/httpService";
import toast from "react-hot-toast";
import "./style.scss";
import { DataGrid } from "@mui/x-data-grid";
import { Typography, Button, Select, MenuItem, FormControl } from "@mui/material";
import { backendUrlImages } from "../../constant/config";

const ActiveRegistrations = () => {
  const [registrationsData, setRegistrationsData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [isUploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [updatingStatus, setUpdatingStatus] = useState({});

  const BaseUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  const getTotalDoctorsCount = (rowData) => {
    if (!rowData || !rowData.number_of_doctors_per_category) return 0;
    
    const doctorsData = rowData.number_of_doctors_per_category;
    
    if (Array.isArray(doctorsData)) {
      return doctorsData.reduce((sum, count) => sum + Number(count || 0), 0);
    } else if (typeof doctorsData === 'object' && doctorsData !== null) {
      return Object.values(doctorsData).reduce((sum, count) => sum + Number(count || 0), 0);
    }
    return 0;
  };

  const fetchRegistrations = async (
    page = 1,
    search = "",
    pageSize = 10
  ) => {
    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/group-registrations`,
        params: {
          per_page: pageSize,
          page,
          search,
        },
        headers: { Authorization: `Bearer ${token}` },
        onSuccess: (data) => {
          const list =
            data?.data ||
            data?.registrations?.data ||
            data?.registrations ||
            [];

          setRegistrationsData(Array.isArray(list) ? list : []);

          const total =
            Number(
              data?.total ||
                data?.meta?.total ||
                data?.registrations?.total ||
                0
            ) || list.length;

          setTotalRecords(total);
          setErrorMsg("");
        },
        onError: (error) => {
          console.error("Fetch registrations error:", error);
          setErrorMsg(error?.message || "Failed to fetch registrations.");
        },
        withToast: true,
      });
    } catch (error) {
      console.error("Fetch registrations try/catch error:", error);
      setErrorMsg("Failed to fetch registrations.");
    }
  };

  // ✅ FINAL: Alert + Page Refresh
  const handleStatusUpdate = async (rowId, newStatus) => {
    setUpdatingStatus((prev) => ({ ...prev, [rowId]: true }));
    
    try {
      await httpService({
        method: "PUT",
        url: `${BaseUrl}/status-update-group-registrations/${rowId}`,
        data: {
          is_active: newStatus === "approve" ? 1 : 0,
        },
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        onSuccess: () => {
          alert("Status updated successfully");
          window.location.reload(); // ✅ Full page refresh
        },
        onError: (error) => {
          console.error("Status update error:", error);
          toast.error(`Failed to update status: ${error?.message || "Unknown error"}`);
        },
      });
    } catch (error) {
      console.error("Status update try/catch error:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const handleUploadClick = () => {
    setFile(null);
    setUploadDialogOpen(true);
  };

  const handleFileUpload = async () => {
    if (!file) {
      toast.error("Please select a file first.");
      return;
    }

    const formData = new FormData();
    formData.append("excelFile", file);

    try {
      await httpService({
        method: "POST",
        url: `${BaseUrl}/admin/excel-upload`,
        headers: { Authorization: `Bearer ${token}` },
        data: formData,
        onSuccess: () => {
          toast.success("File uploaded successfully!");
          const currentPage = paginationModel.page + 1;
          fetchRegistrations(currentPage, "", paginationModel.pageSize);
        },
        onError: (error) =>
          console.error("Failed to upload file:", error),
      });
    } catch (error) {
      console.error("Failed to upload file:", error);
    }

    setUploadDialogOpen(false);
  };

  const handleViewDetails = async (id) => {
    if (!id) return;
    setDetailsLoading(true);
    setDetailsData(null);
    setDetailsOpen(true);

    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/group-registration/${id}`,
        headers: { Authorization: `Bearer ${token}` },
        onSuccess: (data) => {
          const reg = data?.registration || {};
          setDetailsData(reg);
        },
        onError: (error) => {
          console.error("Fetch details error:", error);
          toast.error("Failed to load details");
        },
      });
    } catch (error) {
      console.error("Fetch details try/catch error:", error);
      toast.error("Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    const apiPage = paginationModel.page + 1;
    fetchRegistrations(apiPage, "", paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize]);

  const columns = [
    {
      field: "user_name",
      headerName: "User Name",
      flex: 1,
      minWidth: 180,
      cellClassName: "centered-cell",
    },
    {
      field: "user_email",
      headerName: "Email",
      flex: 1,
      minWidth: 220,
      cellClassName: "centered-cell",
    },
    {
      field: "organization_name",
      headerName: "Organization",
      flex: 1,
      minWidth: 200,
      cellClassName: "centered-cell",
    },
    {
      field: "contact_person",
      headerName: "Contact Person",
      flex: 1,
      minWidth: 180,
      cellClassName: "centered-cell",
    },
    {
      field: "total_doctors",
      headerName: "Number of Doctors",
      flex: 1,
      minWidth: 160,
      cellClassName: "centered-cell",
      renderCell: (params) => {
        if (!params || !params.row) return <span>0</span>;
        
        const count = getTotalDoctorsCount(params.row);
        return (
          <strong style={{ color: count > 0 ? "#1976d2" : "#666" }}>
            {count || 0}
          </strong>
        );
      },
    },
    {
      field: "excel_file",
      headerName: "Excel File",
      flex: 1,
      minWidth: 150,
      cellClassName: "centered-cell",
      renderCell: (params) => (
        <span>
          {params?.value ? (
            <a
              href={`${backendUrlImages}${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", color: "blue" }}
            >
              View Excel
            </a>
          ) : (
            "No File"
          )}
        </span>
      ),
    },
    {
      field: "update_deadline",
      headerName: "Update Deadline",
      flex: 1,
      minWidth: 150,
      cellClassName: "centered-cell",
    },
    {
      field: "last_update",
      headerName: "Last Update By User",
      flex: 1,
      minWidth: 180,
      cellClassName: "centered-cell",
      renderCell: (params) => {
        const date = params?.value ? new Date(params.value) : null;
        const formattedDate = date
          ? `${date.getFullYear()}-${(date.getMonth() + 1)
              .toString()
              .padStart(2, "0")}-${date
              .getDate()
              .toString()
              .padStart(2, "0")}`
          : "No Date";
        return <span>{formattedDate}</span>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      cellClassName: "centered-cell",
      renderCell: (params) => {
        const row = params.row;
        const isFreePayment = row.payment_status === "free";
        const currentStatus = row.is_active === 1 ? "approve" : "disapprove";
        
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isFreePayment ? (
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={currentStatus}
                  onChange={(e) => handleStatusUpdate(row.id, e.target.value)}
                  disabled={updatingStatus[row.id]}
                  sx={{ 
                    fontSize: "0.875rem",
                    "& .MuiSelect-select": {
                      padding: "4px 8px",
                    }
                  }}
                >
                  <MenuItem value="approve">
                    {updatingStatus[row.id] ? "Approving..." : "Approve"}
                  </MenuItem>
                  <MenuItem value="disapprove">
                    {updatingStatus[row.id] ? "Disapproving..." : "Disapprove"}
                  </MenuItem>
                </Select>
              </FormControl>
            ) : (
              <span style={{ 
                color: row.is_active === 1 ? "#4caf50" : "#f44336",
                fontWeight: "bold"
              }}>
                {row.is_active === 1 ? "Active" : "Inactive"}
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleViewDetails(params?.row?.id)}
        >
          View
        </Button>
      ),
    },
  ];

  const rows = registrationsData.map((row, index) => ({
    id: row.id ?? row.user_email ?? index,
    user_name: row.user_name || row.user?.name || "N/A",
    user_email: row.user_email || row.user?.email || "N/A",
    contact_phone: row.contact_phone || row.user?.phone_number || "N/A",
    organization_name: row.organization_name || "N/A",
    contact_person: row.contact_person || "N/A",
    number_of_doctors_per_category: row.number_of_doctors_per_category || null,
    number_of_doctors: row.number_of_doctors || 0,
    excel_file: row.excel_file || null,
    update_deadline: row.update_deadline || "N/A",
    last_update: row.last_update,
    total_amount: row.total_amount || 0,
    payment_status: row.payment_status || "pending",
    is_active: row.is_active || 0,
  }));

  return (
    <div
      style={{
        borderRadius: "8px",
        width: "100%",
        maxWidth: "1700px",
        padding: "20px",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#c62828",
          fontWeight: "bold",
          fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
          textAlign: "center",
        }}
      >
        Group Registrations
      </Typography>

      <div style={{ height: 600, width: "100%", marginTop: 20 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          rowCount={totalRecords}
          pageSizeOptions={[10]}
          checkboxSelection
          disableRowSelectionOnClick
          autoHeight={false}
          hideFooterPagination
          hideFooterSelectedRowCount
        />
      </div>

      {isUploadDialogOpen && (
        <Dialog
          viewHeader={true}
          header="Upload Excel File"
          open={isUploadDialogOpen}
          setOpen={setUploadDialogOpen}
          className="dialog-file"
        >
          <div className="dialog-content">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
            <div className="dialog-actions">
              <button className="save-button" onClick={handleFileUpload}>
                Save
              </button>
              <button
                className="cancel-button"
                onClick={() => setUploadDialogOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {detailsOpen && (
        <Dialog
          viewHeader={true}
          header="Registration Details"
          open={detailsOpen}
          setOpen={setDetailsOpen}
          className="dialog-details"
        >
          <div style={{ padding: "16px", minWidth: "360px" }}>
            {detailsLoading && <p>Loading...</p>}

            {!detailsLoading && detailsData && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                <p><strong>User Name:</strong> {detailsData.user_name}</p>
                <p><strong>Email:</strong> {detailsData.user_email}</p>
                <p><strong>Organization:</strong> {detailsData.organization_name}</p>
                <p><strong>Contact Person:</strong> {detailsData.contact_person}</p>
                <p><strong>Country:</strong> {detailsData.country_of_residence}</p>

                <p><strong>Total Doctors:</strong> <strong>{getTotalDoctorsCount(detailsData)}</strong></p>
                <p><strong>No. of Doctors per Category:</strong></p>
                {detailsData.number_of_doctors_per_category &&
                  Object.entries(
                    detailsData.number_of_doctors_per_category
                  ).map(([cat, count]) => (
                    <p key={cat} style={{ marginLeft: 12 }}>
                      • {cat}: {Number(count)}
                    </p>
                  ))}

                <p><strong>Total Amount:</strong> ${detailsData.total_amount || 0}</p>
                <p><strong>Payment Status:</strong> {detailsData.payment_status}</p>
                <p><strong>Active:</strong> {detailsData.is_active ? "Yes" : "No"}</p>

                <p><strong>Update Deadline:</strong> {detailsData.update_deadline}</p>
                <p><strong>Last Update:</strong> {detailsData.last_update}</p>

                <p><strong>Packages:</strong></p>
                {Array.isArray(detailsData.packages) &&
                  detailsData.packages.map((pkg) => (
                    <div key={pkg.id} style={{ marginLeft: 12 }}>
                      <p>• {pkg.package_name}</p>
                      <p style={{ marginLeft: 16 }}>
                        Type: {pkg.type} | Price / person: ${pkg.price_per_person || 0}
                      </p>
                    </div>
                  ))}

                <p><strong>Created At:</strong> {detailsData.created_at}</p>
                <p><strong>Updated At:</strong> {detailsData.updated_at}</p>
              </div>
            )}
          </div>
        </Dialog>
      )}

      {errorMsg && (
        <Typography color="error" sx={{ mt: 2 }}>
          {errorMsg}
        </Typography>
      )}
    </div>
  );
};

export default ActiveRegistrations;